import { useSwanStyleKeys, useSwanFontKeys, useUserContextTracker, useLogger, useUserContext } from '@vp/ubik-context'
import { BoundedContent, SWAN_STYLE_KEY_MAP, SwanFontNameEnum } from '@vp/swan'
import { ContentfulContextProvider, DebugSection, ContentfulLink } from '@vp/mcs-react-tools'
import { Banner } from './Banner'
import { TrackingProvider } from '../../hooks/trackingContext'
import { FragmentLogger } from '@vp/ubik-logging'
import type { HomepageBannerProps } from '../../types/homepageBanner'
import type { TrackUsedContextProps } from '../../types/sharedTypes'

export const HomepageBanner = (props: HomepageBannerProps) => {
  useSwanFontKeys([SwanFontNameEnum.GraphikRegular, SwanFontNameEnum.GraphikMedium, SwanFontNameEnum.Tiempos, SwanFontNameEnum.TiemposRegular])
  useSwanStyleKeys([
    SWAN_STYLE_KEY_MAP.core,
    SWAN_STYLE_KEY_MAP.button,
    SWAN_STYLE_KEY_MAP.hidden,
    SWAN_STYLE_KEY_MAP.visible,
    SWAN_STYLE_KEY_MAP.utility,
    SWAN_STYLE_KEY_MAP.banner,
  ])

  const logger = useLogger() as FragmentLogger

  logger.setContext({
    ...props,
    name: 'banner-fragment',
  })

  logger.info('Rendering Banner Fragment')

  const tracker = useUserContextTracker()
  const userContext = useUserContext()
  const EXPERIMENT_VARIATION = userContext.experiments?.['next_best_action_panel_v2_fy26']

  if (EXPERIMENT_VARIATION !== undefined && ['en-us', 'en-gb', 'en-au'].includes(props.locale.toLowerCase())) {
    trackUsedContext({ experimentKey: 'next_best_action_panel_v2_fy26', variationKey: EXPERIMENT_VARIATION }, tracker)
  }

  trackUsedContext(props, tracker)

  const debugAvailability = {
    merchandisingCampaign: props.merchandisingCampaign,
    experimentKey: props.experimentKey,
    variationKey: props.variationKey,
    decision: props.decision,
  }
  const search = typeof window !== 'undefined' ? window.location.search : ''

  return (
    <ContentfulContextProvider space={props.contentfulMetadata.spaceId}>
      {!props.usesMap && (
        <>
          <ContentfulLink id={props.contentfulMetadata?.contentful_id} />
          <DebugSection contentfulMetadata={props.contentfulMetadata} availability={debugAvailability} search={search} />
        </>
      )}
      <TrackingProvider canonicalUrl="https://www.vistaprint.com">
        <BoundedContent
          paddingX="0"
          mb={props.bottomSpaceParam ? props.bottomSpaceParam : 'between-sections'}
          style={props.nba ? { overflow: 'hidden', borderRadius: 'var(--swan-sem-border-radius-strong)' } : {}}
        >
          <Banner
            pageZone={props.bannerZone}
            mapMetadata={props.mapMetadata}
            bannerProps={props.bannerData}
            locale={props.locale}
            usesMap={props.usesMap}
            ubikTracking={props.ubikTracking}
          />
        </BoundedContent>
      </TrackingProvider>
    </ContentfulContextProvider>
  )
}

function trackUsedContext(props: TrackUsedContextProps, tracker: ReturnType<typeof useUserContextTracker>) {
  if (props.experimentKey) {
    tracker({
      type: 'experiment',
      name: props.experimentKey,
      variation: props.variationKey,
    })
  }
  if (props.decision) {
    tracker({
      type: 'treatment',
      name: props.decision,
    })
  }
  if (props.merchandisingCampaign) {
    tracker({
      type: 'campaign',
      name: props.merchandisingCampaign,
    })
  }
}
